import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { TextValidator } from 'react-material-ui-form-validator';
import { IStandard2Autocomplete } from '../../_common/interface/autocomplete/iStandard2Autocomplete';
import { IDropdownData } from '../../_common/interface/dropdown/iDropdownData.interface';
import { ParentId } from '../../_services/dropdown.service';


export default function StandardMultipleAutoComplete(params: IStandard2Autocomplete) {

    const [value, setValue] = useState<IDropdownData[] | IDropdownData | null>(params.multiple ? [] : null);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [items, setItems] = useState<IDropdownData[]>([]);
    const [excludedIds, setExcludedIds] = useState<Array<number | string>>(params.excludedIds || []);

    const selectedIds = new Set(
        params.multiple
            ? (value as IDropdownData[] | null)?.map(v => String(v[params.valueField ?? "id"])) ?? []
            : (value as IDropdownData | null)?.[params.valueField ?? "id"] != null ? [String((value as IDropdownData)[params.valueField ?? "id"])] : []
    );
    const excluded = new Set((excludedIds ?? []).map(x => String(x)));

    const filteredOptions = items.filter(m => selectedIds.has(String(m[params.valueField ?? "id"])) || !excluded.has(String(m[params.valueField ?? "id"])));

    const hasSelection = params.multiple
        ? Array.isArray(value) && value.length > 0
        : !!(value as any)?.[params.valueField ?? "id"];

    const parentKey = React.useMemo(() => {
        if (Array.isArray(params.parentId)) {
            // sort to avoid order-only changes if you want
            const arr = [...params.parentId].map(p => `${p.propertyName}:${p.value}`);
            return arr.join('|');
        }
        return params.parentId != null ? String(params.parentId) : '';
    }, [params.parentId]);


    const fetchItems = React.useCallback((pId?: ParentId) => {
        setLoading(true);
        return params.itemsLoader?.(pId as any)
            .then(json => {
                if (!!params.autoSelectSingle && !!json && json.length == 1) {
                    changeInput(json[0])
                }

                setItems(Array.isArray(json) ? json : [])
            })
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [params.itemsLoader]);

    useEffect(() => {
        if (params.excludedIds !== excludedIds) {
            setExcludedIds(params.excludedIds ? params.excludedIds : []);
        }
    }, [params.excludedIds]);

    useEffect(() => {
        if (params.hasParent === true) {
            if (params.parentId && parentKey) {
                fetchItems(params.parentId);
            } else if (items.length > 0) {
                setItems([]);
                changeInput(null);
            }
        } else {
            fetchItems();
        }
    }, [parentKey, params.hasParent]);

    useEffect(() => {
        if (!items) return;

        const byId = new Map(items.map(i => [String(i[params.valueField ?? "id"]), i]));
        const ext = params.value;

        if (params.multiple) {
            const ids = normalizeToIdArray(ext);
            const selected = ids.map(id => byId.get(String(id))).filter(Boolean) as IDropdownData[];
            setValue(selected);
        } else {
            const id = normalizeToSingleId(ext);
            const selected = id != null ? byId.get(String(id)) ?? null : null;
            setValue(selected);
        }
    }, [items, params.value, params.multiple]);


    function changeInput(newValue: IDropdownData | IDropdownData[] | null) {
        setValue(newValue);

        let out: any;
        if (params.multiple) {
            const arr = Array.isArray(newValue) ? newValue : [];
            const ids = arr.map(v => v[params.valueField ?? "id"]);
            out = params.asCsv ? ids.join(', ') : ids;
        } else {
            const single = (newValue as IDropdownData | null)?.[params.valueField ?? "id"] ?? null;
            out = params.asCsv ? (single == null ? '' : String(single)) : single;
        }

        params.onChange?.({ target: { ...newValue, name: params.name, value: out } }, newValue, params.name);
    }

    function normalizeToIdArray(ext: any): Array<string | number> {
        if (ext == null || ext === '') return [];
        if (Array.isArray(ext)) return ext;
        if (typeof ext === 'string') return ext.split(',').map(s => s.trim()).filter(Boolean);
        return [ext];
    }

    function normalizeToSingleId(ext: any): string | number | null {
        if (ext == null || ext === '') return null;
        if (Array.isArray(ext)) return ext[0] ?? null;
        return ext;
    }

    return (
        <>
            <Autocomplete
                fullWidth={params.fullWidth}
                open={params.open}
                onOpen={params.onOpen}
                disableClearable={params.disableClearable ? true : false}
                options={filteredOptions}
                getOptionLabel={(option) => option.name}
                multiple={!!params.multiple}
                value={(value as any) ?? (params.multiple ? [] : null)}
                onChange={(_, newValue) => changeInput(newValue as any)}
                inputValue={inputValue || ''}
                onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                disabled={params.disabled}
                renderInput={(props) =>
                    params.hasFormsValidator === false ? (
                        <TextField
                            value={inputValue}
                            {...props}
                            label={`${params.label} ${params.required === true ? '*' : ''}`}
                            autoComplete="new-password"
                            name={`${params.name}`}
                            variant={params.variant || 'outlined'}
                            type="text"
                            disabled={params.disabled || false}
                            InputProps={{
                                ...props.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {props.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    ) : (
                        <TextValidator
                            value={inputValue}
                            {...props}
                            label={`${params.label} ${params.required === true ? '*' : ''}`}
                            autoComplete="new-password"
                            name={`${params.name}`}
                            validators={params.required ? (hasSelection ? [] : ['required']) : []}
                            errorMessages={params.required ? (hasSelection ? [] : ['This field is required']) : []}
                            variant={params.variant || 'outlined'}
                            type="text"
                            disabled={params.disabled || false}
                            helperText={params.helperText}
                            InputProps={{
                                ...props.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {props.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )
                }
            />

        </>
    );
}
